import React from "react";
import { useSelector } from "react-redux";
import {
  getSelectedSegment,
  getSelectedSegmentCharCount,
  getWheelSegments,
} from "../../redux/selectors/segmentBuilderSelectors";
import {
  SEGMENT_ADAPTIVE_ICONS_ENABLED,
  SEGMENT_ICON_SECTION_ENABLED,
  SEGMENT_FILL_UNIFIED_ENABLED,
  SEGMENT_STYLING_V2_ENABLED,
} from "../../constants/featureFlags";
import SegmentFillUnified from "../SegmentBuilder/Shared/SegmentFillUnified";
import {
  computeSegmentLabel,
  orderSegmentsByPosition,
  isUnnamedSegment,
} from "../../utils/segmentLabels";
import Wallpaper from "../SegmentBuilder/Shared/Wallpaper";
import { SegmentContainer, SegmentSubTitle } from "./SegmentSettings.styled";
import SegmentHeader from "./children/SegmentHeader";
import SegmentSize from "./children/SegmentSize";
import SegmentTitle from "./children/SegmentTitle";
import SegmentColor from "./children/SegmentColor";
import SegmentIcon from "./children/SegmentIcon";
import {
  iconElementFromName,
  iconNameFromElement,
} from "./children/segmentIconResolver";
import { Box, LinearProgress, Card } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TextView from "../TextView";
import IyugaTooltip from "../IyugaTooltip";
import { useSegmentPalettes } from "../../hooks/useSegmentPalettes";
import useSegmentViewAspectRatio from "../../hooks/useSegmentViewAspectRatio";

export const charLimitInASegment = 20000;

const SegmentSettings = ({
  handleDeleteSegment,
  handleEmojiPicker,
  handleChange,
  isHideHeader = false,
}) => {
  const selectedArc = useSelector(getSelectedSegment);
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);
  const { fetchSegmentPalette } = useSegmentPalettes();
  const charPercentage = Math.min(
    (selectedSegmentCharCount / charLimitInASegment) * 100,
    100,
  );

  // Determine progress bar and background colors based on percentage
  let progressColor = "#8F38EF";
  let backgroundColor = "#F9FAFB";

  if (charPercentage >= 100) {
    progressColor = "#ED4337";
    backgroundColor = "#FCE5E0";
  } else if (charPercentage >= 80) {
    progressColor = "#E87033";
    backgroundColor = "#FEF5E7";
  }

  const { title, color, icon, iconHidden } = selectedArc?.settings || {};

  // SEGMENT WALLPAPER (stgfix-1007-P0-segment-fill-bleeds-into-wallpaper): the
  // <Wallpaper> accordion below reads/writes its OWN nested `settings.wallpaper`
  // sub-object, independent of Segment Fill's top-level currentBg/bgWallpaper/
  // segmentBgColor/isPattern/pattern* fields — picking a Fill can no longer
  // change what this control shows, and vice versa.
  const {
    wallpaperTransparency,
    wallpaperResizing,
    wallpaperOverlayColor,
    bgWallpaper,
    isPattern,
    selectedColorType,
    patternColor,
    currentBg,
    segmentBgColor,
    patternId,
    resizeMode,
    wallpaperHuePercentage,
    wallpaperHueColor,
  } = selectedArc?.settings?.wallpaper || {};

  // gordon-1002-P1-segment-title-default-as-placeholder (REVERSES ISP-9999 /
  // Gordon 2026-08-27, quoted below): Gordon 2026-10-02 (chat, screenshot of
  // the DEVELOPMENT Segment Settings pane), verbatim: "Segment Title - in the
  // side menu if a segment has a default title (i.e. Segment 1, Segment 2,
  // etc), it should be placeholder text in the field so you don't need to
  // delete it all to rewrite it. It should be rewritten automatically after
  // an individual type. And if it is deleted entirely, the placeholder
  // Segment Name text should show again." The 2026-08-27 decision this
  // replaces had committed "Segment N" as real field VALUE text for exactly
  // the opposite reason (a greyed placeholder "read as unfinished/unset") --
  // Gordon's new call is the opposite trade-off: committed text forced a
  // select-all-and-delete before typing a real name, which is the bug this
  // fixes. N is still recomputed from the CURRENT wheel order on every render
  // (utils/segmentLabels), so a reorder still renumbers the placeholder; a
  // segment with a real user-given name is untouched (value, not placeholder).
  const allSegments = useSelector(getWheelSegments) || [];
  const segmentUnnamed = isUnnamedSegment(selectedArc);
  const computedUnnamedLabel = segmentUnnamed
    ? computeSegmentLabel(selectedArc, orderSegmentsByPosition(allSegments))
    : "";
  const titleFieldValue = segmentUnnamed ? "" : title || "";

  // gordon-0920-editwheel-open-mutates-P0: VIEW ACTIONS NEVER WRITE. This used to
  // run on EVERY Segment select (`selectedArc?.id` change) for any Segment that
  // happened to lack a `paletteId` — silently deriving a palette FROM THE FILL
  // (an image extraction call + savePalette + settings.palette_data write + the
  // "Palette updated from image" Undo toast) as a side effect of merely OPENING
  // the Segment, not of the user replacing its fill. Palette-from-fill now runs
  // ONLY from the actual fill-replace handler (SegmentFillUnified's `commitFill`
  // / SegmentColor's `handleSegmentColorChange` below) — opening a Segment that
  // has no palette yet just shows its plain stored `color` until the fill is
  // genuinely replaced.

  const handleSegmentColorChange = (color) => {
    handleChange("segmentColor", null, color);
    if (selectedArc) fetchSegmentPalette(color, selectedArc?.id);
  };

  // Segment view popup aspect ratio, matched to the crop area dimensions
  // (SegmentView Container: width = max(354px, 25vw), height = min(900px, 85dvh)).
  // F6.9: read live from the viewport via a resize subscription — computing it in
  // the render body left the cropper locked to a stale, pre-resize panel shape.
  const segmentViewAspectRatio = useSegmentViewAspectRatio();

  return (
    <SegmentContainer>
      {!isHideHeader && (
        <SegmentHeader handleDeleteSegment={handleDeleteSegment} />
      )}

      {/* Text Character Limit Component */}
      <Card
        sx={{
          padding: 2,
          backgroundColor: backgroundColor,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #EAECF0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SegmentSubTitle style={{ marginRight: "10px" }}>
              Text Character Limit
            </SegmentSubTitle>

            <IyugaTooltip
              tooltip={`Each Segment can contain up to ${charLimitInASegment} text characters`}
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "#323232", cursor: "pointer" }}
              />
            </IyugaTooltip>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <TextView
              variant="largeText"
              style={{ color: "#000000DE", fontWeight: 700 }}
            >
              {selectedSegmentCharCount.toLocaleString()}
            </TextView>
            <TextView variant="largeText" style={{ color: "#667085" }}>
              out of {charLimitInASegment.toLocaleString()}
            </TextView>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={charPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#EAECF0",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              backgroundColor: progressColor,
            },
          }}
        />
      </Card>

      <SegmentTitle
        handleEmojiPicker={handleEmojiPicker}
        title={titleFieldValue}
        // DL-029: greyed placeholder for an unnamed segment, instead of
        // forcing "Segment N" into the editable value (see the comment
        // above where titleFieldValue/computedUnnamedLabel are computed).
        placeholder={computedUnnamedLabel || undefined}
        onTitleChange={(value) =>
          handleChange("segmentTitle", null, value || "")
        }
      />
      <SegmentSize />

      {/* <SegmentIcon
        selectedIcon={icon}
        onIconSelect={(value) => handleChange('segmentIcon', null, value)}
      /> */}

      {/* SEGMENT_STYLING_V2 double-UI hide (Bucket-B): the legacy Wallpaper
          accordion below is the old per-Segment fill UI. When
          REACT_APP_SEGMENT_STYLING_V2 is ON, the new SegmentStyling fill panel
          owns per-Segment fill, so mounting this legacy accordion too would show
          a duplicate "double fill" UI. Gate it OFF when V2 is ON. Flag OFF
          (default) keeps this pane byte-identical to today. */}
      {!SEGMENT_STYLING_V2_ENABLED && (
      <Wallpaper
        // customCategories={['Patterns', 'Image']}
        showAccordion={true}
        cropAspectRatio={segmentViewAspectRatio}
        huePercentage={wallpaperHuePercentage ?? 0}
        huePercentageChange={(value) =>
          handleChange("wallpaperHuePercentage", null, value)
        }
        hueColor={wallpaperHueColor ?? "#ffffff"}
        hueColorChange={(value) =>
          handleChange("wallpaperHueColor", null, value)
        }
        slider1Value={wallpaperResizing}
        slider1Change={(value) =>
          handleChange("wallpaperResizing", null, value)
        }
        slider1BoxTitle={"Resizing"}
        // segment-environments:F8.2 (2026-08-06): slider1 (Resizing) props were wired here
        // but the shared <Wallpaper> component never rendered slider1 at all — the slider was
        // unreachable regardless of this flag. Fixed in Wallpaper/index.jsx; this caller opts
        // in explicitly since it's the only one with real slider1Value/slider1Change wiring.
        isShowResizeSlider={true}
        slider2Value={wallpaperTransparency}
        slider2Change={(value) =>
          handleChange("wallpaperTransparency", null, value)
        }
        slider2BoxTitle={"Opacity"}
        handleWallpaperOverlayColorChange={(value) =>
          handleChange("addWallpaperOverlayColor", null, value)
        }
        wallpaperOverlayColor={wallpaperOverlayColor}
        handleSelectWallpaper={(value) =>
          handleChange("addBgWallpaper", 0, value)
        }
        handleSelectPattern={(value) => {
          handleChange("addPatternId", 1, value);
        }}
        isPattern={isPattern}
        bgWallpaper={bgWallpaper}
        background={wallpaperOverlayColor}
        handlePatternColorChange={(value) =>
          handleChange("addPatternColor", null, value)
        }
        handlePatternColorInitialChange={(value) =>
          handleChange("addPatternColorInitially", null, value)
        }
        handleSelectColorType={(val) =>
          handleChange("patternColorType", null, val)
        }
        selectedColorType={selectedColorType}
        patternColor={patternColor}
        onBgWallpaperColorChange={(value) =>
          handleChange("segmentBgColor", null, value)
        }
        currentBg={currentBg}
        segmentBgColor={segmentBgColor}
        patternId={patternId}
        // segment-environments:F8.1 (2026-08-05): wallpaperTransparency genuinely drives the
        // rendered wallpaper's CSS opacity (SegmentView/styles.js, pages/Segment/styles.js via
        // wallpaperOpacity()/segmentWallpaperOpacity.js) but this hid the only control that
        // could set it per-Segment. Set explicitly true.
        isShowTransparencySlider={true}
        handleSelectBackgroundResizeMode={(value) =>
          handleChange("addResizeMode", null, value)
        }
        resizeMode={resizeMode}
        handleGradientColor={(value) =>
          handleChange("segmentBgGradientColor", null, value)
        }
      />
      )}
      {/* u6-segment-fill: the stand-alone "Segment Color" accordion below is the
          FRAME colour control. When SEGMENT_FILL_UNIFIED_ENABLED is ON it is
          folded into the unified <SegmentFillUnified> "Segment Fill" section
          (Color / Gradient / Pattern / Image / GIF tabs) rendered here instead;
          OFF (default) keeps this pane byte-identical to today. */}
      {SEGMENT_FILL_UNIFIED_ENABLED ? (
        <SegmentFillUnified segment={selectedArc} />
      ) : (
        <SegmentColor
          showTabs={false}
          onColorChange={(value) => handleSegmentColorChange(value)}
          currentColor={color}
          showSuggestedColorPalette={false}
        />
      )}

      {/* Per-segment icon picker (icon-picker-slice, deck 04-segment-icon-picker).
          Placed directly below Segment Color per the deck. Gated on
          SEGMENT_ADAPTIVE_ICONS_ENABLED — OFF (default) renders nothing, so the
          pane is byte-identical to today. The picker emits a React element; the
          chosen icon PERSISTS as a serializable identifier on segment.settings.icon
          (see segmentIconResolver — a React element cannot JSON round-trip through
          generatePayload). Selecting the dashed "No Icon" tile clears it. The
          searchable grid + "No Icon" tile live in the shared SegmentIcon child;
          ICON_NO_ICON_TILE_ENABLED governs the tile. */}
      {/* Gordon 2026-10-09: the section also needs SEGMENT_ICON_SECTION_ENABLED
          (REACT_APP_SEGMENT_ICON_SECTION) — OFF on Staging + Production. Saved
          icons still render on wheels; only this picker section hides. */}
      {SEGMENT_ADAPTIVE_ICONS_ENABLED && SEGMENT_ICON_SECTION_ENABLED && (
        <Box sx={{ mt: 1 }}>
          <SegmentIcon
            title="Segment Icon"
            selectedIcon={iconElementFromName(icon)}
            onIconSelect={(value) =>
              handleChange("segmentIcon", null, iconNameFromElement(value))
            }
            // Segment-icon-picker (2026-08-18): persisted show/hide toggle that
            // preserves the stored icon. `iconHidden` lives on segment.settings
            // (like iconColor); the wheel render (ViewWheel + SegmentBuilder)
            // withholds the glyph when it is true, without wiping settings.icon.
            hidden={!!iconHidden}
            onToggleHidden={(nextHidden) =>
              handleChange("segmentIconHidden", null, nextHidden)
            }
          />
        </Box>
      )}

      {/* <ColorPalette
        title="Segment Palette"
        disabled
        generateAutomatically={true}
      /> */}

      {/* Segment Environments (flag REACT_APP_SEGMENT_ENVIRONMENTS) used to be
          mounted here, but ISP-9999 #507 (2026-09-01) found the 2026-08-18
          CONSISTENCY-PASS had already re-housed a second "Segment Environment"
          section BELOW Content Lock in the parent
          (SegmentBuilder/Main/SegmentSetting/index.jsx) without removing this
          one, so both rendered simultaneously — two headings on the live
          builder pane. The parent is now the single mount point. */}
    </SegmentContainer>
  );
};

export default SegmentSettings;
